import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import { Form, Button, Alert, Col, Row, Card } from "react-bootstrap";
import { Formik, FormikProps } from "formik";
import getCentersForEventGroup from "../EventGroupAllowedCenterMap";
import Centre from "../models/Centre";
import Axios, { AxiosRequestConfig } from "axios";
import { apiBaseUrl } from "../AppSettings";

enum modeOfPayment {
    CASH = "CASH",
    EFTPOS = "EFTPOS",
}

interface AnnakutSevaFormProps {
    firstName: string;
    lastName: string;
    email: string;
    mobileNumber: string;
    centre: number;
    centerSetInUiState: boolean;
    address: string;
    suburb: string;
    zip: number;
    referenceKaryakar: string;
    modeOfPayment: modeOfPayment;
    SingleThalSevaCount: number;
    KhandThalSevaCount: number;
    SampurnaThalSevaCount: number;
    ReceiptAlreadyIssued: boolean;
}

export default function AnnakutSevaManualEntry() {

    const [authToken, setAuthToken] = useState<string | undefined>(undefined);
    const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
    const [saveError, setSaveError] = useState<string>("");
    const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
    const location = useLocation();

    const defaultFormValues: AnnakutSevaFormProps = {
        firstName: "",
        lastName: "",
        mobileNumber: "",
        email: "",
        centre: 0,
        centerSetInUiState: false,
        address: "",
        suburb: "",
        zip: 0,
        referenceKaryakar: "",
        modeOfPayment: modeOfPayment.CASH,
        SingleThalSevaCount: 0,
        KhandThalSevaCount: 0,
        SampurnaThalSevaCount: 0,
        ReceiptAlreadyIssued: false
    }

    const donorDetailsSchema = Yup.object().shape({
        firstName: Yup.string().required().max(50),
        lastName: Yup.string().required().max(50),
        mobileNumber: Yup.string().matches(/(0|\+\d{1,3})[0-9]{8,10}\b/gm, "Please enter mobile number in 04xxxxxxxx (AU)/ 02XXXXXXXXX / +1567890123").required(),
        email: Yup.string().email().required(),
        centerSetInUiState: Yup.boolean(),
        centre: Yup.number().when("centerSetInUiState", {
            is: false,
            then: Yup.number().required().min(1, "Center is required before submission")
        }),
        suburb: Yup.string().max(100),
        address: Yup.string().max(500),
        zip: Yup.number().max(9999),
        referenceKaryakar: Yup.string().max(100),
        SingleThalSevaCount: Yup.number().min(0).max(5),
        KhandThalSevaCount: Yup.number().min(0).max(5),
        SampurnaThalSevaCount: Yup.number().min(0).max(5),
    });

    useEffect(() => {
        if (!location || !location.state) {
            console.log("No state found in location");
            return;
        }

        // @ts-ignore
        const authToken = location.state.authToken;
        setAuthToken(authToken);
    }, []);

    const renderCentreSelect = (): Array<JSX.Element> => {
        const allowedCenters = getCentersForEventGroup("diwali-annakut-2025");
        const optionsForCentres = new Array<JSX.Element>();

        allowedCenters.forEach(eachCenter => {
            const keyNumeric: number = eachCenter;

            if (isNaN(keyNumeric))
                return;

            optionsForCentres.push(<option key={`Centre-${keyNumeric}`} value={keyNumeric}>{Centre[keyNumeric]}</option>)
        });

        return optionsForCentres;
    }

    const onNextClick = async (values: AnnakutSevaFormProps, { resetForm }: any) => {
        const requestConfig: AxiosRequestConfig = {};
        requestConfig.headers = { 'Authorization': `Bearer ${authToken}` };
        setSaveInProgress(true);
        setSaveError("");
        const apiRequest = { ...values, centre: Number(values.centre), zip: values.zip ? Number(values.zip) : 0, issueReceipt: !values.ReceiptAlreadyIssued };
        try {
            const response = await Axios.post(`${apiBaseUrl}/api/AnnakutManualRegistrations`, apiRequest, requestConfig);
            setSaveInProgress(false);

            if (response.status === 200) {
                setSaveSuccess(true);
                resetForm(); // This will reset the form to initial values
            } else {
                setSaveError(`Error occurred while saving the details. Please try again later.`);
            }
        } catch (error) {
            setSaveInProgress(false);
            setSaveError(`Error occurred while saving the details. Please try again later.`);
        }

    }

    const renderForm = (formikProps: FormikProps<AnnakutSevaFormProps>) => {

        return (<Form onSubmit={formikProps.handleSubmit}>
            <Form.Group controlId="formFirstName">
                <Form.Label>First Name</Form.Label>
                <Form.Control type="text"
                    placeholder="Enter first name"
                    onChange={formikProps.handleChange}
                    value={formikProps.values.firstName}
                    name="firstName" />
                <Form.Text className="text-muted">
                    Please enter your first name.
                </Form.Text>
                {formikProps.touched &&
                    formikProps.touched.firstName &&
                    formikProps.errors.firstName ?
                    (<div className="fade alert alert-danger show">{formikProps.errors.firstName}</div>) : (<React.Fragment />)}
            </Form.Group>

            <Form.Group controlId="formEmail">
                <Form.Label>Last Name</Form.Label>
                <Form.Control type="text"
                    placeholder="Enter last name"
                    name="lastName"
                    onChange={formikProps.handleChange}
                    value={formikProps.values.lastName} />
                <Form.Text className="text-muted">
                    Please enter your last name.
                </Form.Text>
                {formikProps.touched &&
                    formikProps.touched.lastName &&
                    formikProps.errors.lastName ?
                    (<div className="fade alert alert-danger show">{formikProps.errors.lastName}</div>) : (<React.Fragment />)}
            </Form.Group>

            <Form.Group controlId="formEmail">
                <Form.Label>Email address</Form.Label>
                <Form.Control type="email"
                    placeholder="Enter email"
                    name="email"
                    value={formikProps.values.email}
                    onChange={formikProps.handleChange} />
                <Form.Text className="text-muted">
                    Please enter your email id. Receipt will be sent to the email id entered here.
                </Form.Text>
                {formikProps.touched &&
                    formikProps.touched.email &&
                    formikProps.errors.email ?
                    (<div className="fade alert alert-danger show">{formikProps.errors.email}</div>) : (<React.Fragment />)}
            </Form.Group>

            <Form.Group controlId="formMobile">
                <Form.Label>Mobile number</Form.Label>
                <Form.Control type="text"
                    placeholder="04XXXXXXXX(AU)/02XXXXXXXXX(NZ)/+1XXXXXXXXXX"
                    name="mobileNumber"
                    value={formikProps.values.mobileNumber}
                    onChange={formikProps.handleChange} />
                <Form.Text className="text-muted">
                    Please enter your mobile number starting with 04 (AU) / 02 (NZ) / (+CountryCode) Mobile Number.
                </Form.Text>
                {formikProps.touched &&
                    formikProps.touched.mobileNumber &&
                    formikProps.errors.mobileNumber ?
                    (<div className="fade alert alert-danger show">{formikProps.errors.mobileNumber}</div>) : (<React.Fragment />)}
            </Form.Group>

            <Form.Group controlId="formCentre">
                <Form.Label>Select Centre</Form.Label>
                <Form.Control as="select"
                    name="centre"
                    value={formikProps.values.centre}
                    onChange={formikProps.handleChange} >
                    <option></option>
                    {renderCentreSelect()}
                </Form.Control>
                <Form.Text className="text-muted">
                    Please select the centre.
                </Form.Text>
                {formikProps.touched &&
                    formikProps.touched.centre &&
                    formikProps.errors.centre ?
                    (<div className="fade alert alert-danger show">{formikProps.errors.centre}</div>) : (<React.Fragment />)}
            </Form.Group>

            <Form.Group controlId="formModeOfPayment">
                <Form.Label>Mode of Payment</Form.Label>
                <Form.Control as="select"
                    name="modeOfPayment"
                    value={formikProps.values.modeOfPayment}
                    onChange={formikProps.handleChange} >
                    <option value={modeOfPayment.CASH}>{modeOfPayment.CASH}</option>
                    <option value={modeOfPayment.EFTPOS}>{modeOfPayment.EFTPOS}</option>
                </Form.Control>
            </Form.Group>

            <Form.Group controlId="formRefKaryakar">
                <Form.Label>Reference Karyakar</Form.Label>
                <Form.Control type="text"
                    name="referenceKaryakar"
                    onChange={formikProps.handleChange} />
                <Form.Text className="text-muted">
                    Please enter the Reference Karyakar.
                </Form.Text>
                {formikProps.touched &&
                    formikProps.touched.referenceKaryakar &&
                    formikProps.errors.referenceKaryakar ?
                    (<div className="fade alert alert-danger show">{formikProps.errors.referenceKaryakar}</div>) : (<React.Fragment />)}
            </Form.Group>

            <Form.Group as={Row} controlId="frmGrpThalSevaCounts">
                <Col sm={12} md={4}>
                    <Form.Label>Single Thal Seva Count</Form.Label>
                    <Form.Control type="number" name="SingleThalSevaCount" value={formikProps.values.SingleThalSevaCount} onChange={formikProps.handleChange} />
                    <Form.Text className="text-muted">
                        Please enter the Single Thal Seva Count.
                    </Form.Text>
                    {formikProps.touched &&
                        formikProps.touched.SingleThalSevaCount &&
                        formikProps.errors.SingleThalSevaCount ?
                        (<div className="fade alert alert-danger show">{formikProps.errors.SingleThalSevaCount}</div>) : (<React.Fragment />)}
                </Col>
                <Col sm={12} md={4}>
                    <Form.Label>Khand Thal Seva Count</Form.Label>
                    <Form.Control type="number" name="KhandThalSevaCount" value={formikProps.values.KhandThalSevaCount} onChange={formikProps.handleChange} />
                    <Form.Text className="text-muted">
                        Please enter the Khand Thal Seva Count.
                    </Form.Text>
                    {formikProps.touched &&
                        formikProps.touched.KhandThalSevaCount &&
                        formikProps.errors.KhandThalSevaCount ?
                        (<div className="fade alert alert-danger show">{formikProps.errors.KhandThalSevaCount}</div>) : (<React.Fragment />)}
                </Col>
                <Col sm={12} md={4}>
                    <Form.Label>Sampurna Thal Seva Count</Form.Label>
                    <Form.Control type="number" name="SampurnaThalSevaCount" value={formikProps.values.SampurnaThalSevaCount} onChange={formikProps.handleChange} />
                    <Form.Text className="text-muted">
                        Please enter the Sampurna Thal Seva Count.
                    </Form.Text>
                    {formikProps.touched &&
                        formikProps.touched.SampurnaThalSevaCount &&
                        formikProps.errors.SampurnaThalSevaCount ?
                        (<div className="fade alert alert-danger show">{formikProps.errors.SampurnaThalSevaCount}</div>) : (<React.Fragment />)}
                </Col>
            </Form.Group>

            <Form.Group as={Row} controlId="frmGrpAddress">
                <Col sm={12} md={6}>
                    <Form.Label>Street Address</Form.Label>
                    <Form.Control type="text" name="address" value={formikProps.values.address} onChange={formikProps.handleChange} />
                    {formikProps.touched &&
                        formikProps.touched.address &&
                        formikProps.errors.address ?
                        (<div className="fade alert alert-danger show">{formikProps.errors.address}</div>) : (<React.Fragment />)}
                </Col>
                <Col sm={12} md={3}>
                    <Form.Label>Suburb</Form.Label>
                    <Form.Control type="text" name="suburb" value={formikProps.values.suburb} onChange={formikProps.handleChange} />
                    {formikProps.touched &&
                        formikProps.touched.suburb &&
                        formikProps.errors.suburb ?
                        (<div className="fade alert alert-danger show">{formikProps.errors.suburb}</div>) : (<React.Fragment />)}
                </Col>
                <Col sm={12} md={3}>
                    <Form.Label>Zip</Form.Label>
                    <Form.Control type="text" name="zip" value={formikProps.values.zip} onChange={formikProps.handleChange} />
                    {formikProps.touched &&
                        formikProps.touched.zip &&
                        formikProps.errors.zip ?
                        (<div className="fade alert alert-danger show">{formikProps.errors.zip}</div>) : (<React.Fragment />)}
                </Col>
            </Form.Group>

           <Form.Group controlId="formReceiptAlreadyIssued">
                <Form.Label>Receipt Already Issued</Form.Label>
                <Form.Control type="checkbox"
                    name="ReceiptAlreadyIssued"
                    checked={formikProps.values.ReceiptAlreadyIssued}
                    onChange={formikProps.handleChange} />
                <Form.Text className="text-muted">
                    Is Donor receipt already issued for this registration?
                </Form.Text>
                {formikProps.touched &&
                    formikProps.touched.ReceiptAlreadyIssued &&
                    formikProps.errors.ReceiptAlreadyIssued ?
                    (<div className="fade alert alert-danger show">{formikProps.errors.ReceiptAlreadyIssued}</div>) : (<React.Fragment />)}
            </Form.Group>

            <Button variant="primary" type="submit" size="lg" disabled={saveInProgress || saveSuccess}>
                Submit
            </Button>
        </Form>);
    }

    const renderDonorDetailsForm = () => {
        return (<Formik
            enableReinitialize={true}
            initialValues={defaultFormValues}
            onSubmit={onNextClick}
            render={renderForm}
            validationSchema={donorDetailsSchema}
        />);
    }

    return (<div>
        <h1>Annakut Seva Manual Entry</h1>
        {renderDonorDetailsForm()}
        <div style={{ marginTop: "20px" }}>
            {saveInProgress ? <Alert variant="info" onClose={() => setSaveInProgress(false)} dismissible>Saving your details. Please wait...</Alert> : <React.Fragment />}
            {saveError ? <Alert variant="danger" onClose={() => setSaveError("")} dismissible>{saveError}</Alert> : <React.Fragment />}
            {saveSuccess ? <Alert variant="success" onClose={() => setSaveSuccess(false)} dismissible>Your details have been saved successfully.</Alert> : <React.Fragment />}
        </div>
    </div>);
}


